import { AlertRepository, AlertListOptions, AlertListResponse } from '../../domain/repositories/AlertRepository';
import { Alert, AlertCondition, AlertEmail, AlertSMS } from '../../domain/entities/Alert';
import { Recipient } from '../../domain/value-objects/Recipient';
import { ILoggerService } from '../../domain/services/LoggerService';
import { UserContext } from '../../domain/value-objects/UserContext';
import { BaseFirebaseRepository, FilterCondition } from './BaseFirebaseRepository';

export class FirebaseAlertRepository extends BaseFirebaseRepository<Alert> implements AlertRepository {
  constructor(logger: ILoggerService) {
    super('alerts', logger);
  }

  protected convertFirestoreToEntity(id: string, data: any): Alert {
    return new Alert(
      id,
      data.name,
      data.enabled,
      this.convertConditionsFromFirestore(data.conditions || []),
      this.convertEmailFromFirestore(data.email || {}),
      this.convertSMSFromFirestore(data.sms || {}),
      data.sensorIds || [],
      data.organizationId,
      data.repeat,
      data.delayedNotification,
      this.convertFirestoreDate(data.createdAt),
      this.convertFirestoreDate(data.updatedAt),
      this.convertRecipientsFromFirestore(data.recipients)
    );
  }

  protected getDefaultSortField(): string {
    return 'name';
  }

  protected mapSortField(sortBy: string): string {
    switch (sortBy) {
      case 'enabled':
        return 'enabled';
      case 'createdAt':
        return 'createdAt';
      case 'name':
      default:
        return 'name';
    }
  }

  protected applyLocationScopeFilters(_locationIds: string[]): FilterCondition[] {
    return [];
  }

  protected applySearchFilter(alerts: Alert[], search: string): Alert[] {
    const searchLower = search.toLowerCase();
    return alerts.filter(alert =>
      alert.name.toLowerCase().includes(searchLower)
    );
  }



  async findAll(options: AlertListOptions = {}, userContext?: UserContext): Promise<AlertListResponse> {
    const {
      organizationId,
      ...queryOptions
    } = options;

    const response = await this.executeQuery(userContext, queryOptions, [], [], organizationId);
    
    return {
      alerts: response.items,
      totalCount: response.totalCount,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
      pageSize: response.pageSize
    };
  }

  async countWithUserContext(userContext?: UserContext): Promise<number> {
    return this.countWithFilters(userContext);
  }


  private convertConditionsFromFirestore(conditionsData: any[]): AlertCondition[] {
    if (!Array.isArray(conditionsData)) {
      return [];
    }

    return conditionsData.map(condition => ({
      field: condition.field || '',
      operator: condition.operator || '==',
      threshold: condition.threshold || 0
    }));
  }

  private convertEmailFromFirestore(emailData: any): AlertEmail {
    if (!emailData || typeof emailData !== 'object') {
      return { enabled: false, emails: [] };
    }

    return {
      enabled: emailData.enabled ?? false,
      emails: Array.isArray(emailData.emails) ? emailData.emails : []
    };
  }

  private convertSMSFromFirestore(smsData: any): AlertSMS {
    if (!smsData || typeof smsData !== 'object') {
      return { enabled: false, phoneNumbers: [] };
    }

    return {
      enabled: smsData.enabled ?? false,
      phoneNumbers: Array.isArray(smsData.phoneNumbers) ? smsData.phoneNumbers : []
    };
  }

  private convertRecipientsFromFirestore(recipientsData: any): Recipient[] | undefined {
    if (!Array.isArray(recipientsData)) {
      return undefined;
    }

    const recipients: Recipient[] = [];
    for (const raw of recipientsData) {
      if (!raw || typeof raw !== 'object') continue;
      try {
        recipients.push(new Recipient({
          contactId: raw.contactId,
          name: raw.name,
          email: raw.email,
          phone: raw.phone,
          viaEmail: !!raw.viaEmail,
          viaSms: !!raw.viaSms
        }));
      } catch (error) {
        this.logger.logRepositoryError('FirebaseAlertRepository', 'convertRecipient', 0, error);
      }
    }
    return recipients;
  }

}